import { DEFAULT_ACTIVITY_MINUTES, formatTime } from '@erp/shared';
import { Lock, TriangleAlert } from 'lucide-react';

import {
  addDays,
  dayLabel,
  todayIn,
  weekday,
  zonedParts,
  type ActivityItem,
  type CalendarItem,
} from '../../lib/activities-api.ts';
import { cn } from '../../lib/cn.ts';
import { BLOCK } from './type-style.tsx';

/*
 * Our own calendar on CSS grid (no library, no inline styles: the CSP forbids them).
 * The day runs 06:00–22:00 in 15-minute rows; blocks are placed with row-start/span
 * classes generated by `@source inline(...)` in styles.css. Activities that overlap
 * share the column side by side (lanes) and are outlined with a warning mark;
 * another person's private activity is a gray "Ocupado" block.
 */

export type CalendarMode = 'day' | 'week' | 'month';

const START_HOUR = 6;
const END_HOUR = 22;
const SLOT_MINUTES = 15;
const ROWS = ((END_HOUR - START_HOUR) * 60) / SLOT_MINUTES;
const MAX_LANES = 6;

// Literal class names so Tailwind generates them (see @source inline in styles.css).
const ROW_START = Array.from({ length: ROWS + 1 }, (_, i) => `row-start-${String(i + 1)}`);
const ROW_SPAN = Array.from({ length: ROWS }, (_, i) => `row-span-${String(i + 1)}`);
const COL_START = Array.from({ length: MAX_LANES }, (_, i) => `col-start-${String(i + 1)}`);
const COL_SPAN = Array.from({ length: MAX_LANES }, (_, i) => `col-span-${String(i + 1)}`);
const GRID_COLS = Array.from({ length: MAX_LANES }, (_, i) => `grid-cols-${String(i + 1)}`);

/** First day shown: the Monday of the week, or the Monday before the 1st of the month. */
export function rangeOf(mode: CalendarMode, anchor: string): { from: string; days: number } {
  if (mode === 'day') return { from: anchor, days: 1 };
  if (mode === 'week') return { from: addDays(anchor, -weekday(anchor)), days: 7 };
  const first = `${anchor.slice(0, 8)}01`;
  return { from: addDays(first, -weekday(first)), days: 42 };
}

interface Column {
  key: string;
  label: string;
  date: string;
  /** Only this member's items (team view of a single day). */
  membershipId?: string;
}

export function Calendar({
  mode,
  anchor,
  items,
  tz,
  members,
  onOpen,
  onPickDay,
}: {
  mode: CalendarMode;
  anchor: string;
  items: CalendarItem[];
  tz: string;
  /** Shown people (with names): a single day with several people gets a column each. */
  members: { membershipId: string; name: string }[];
  onOpen: (activity: ActivityItem) => void;
  onPickDay: (date: string) => void;
}) {
  const { from, days } = rangeOf(mode, anchor);
  if (mode === 'month') {
    return (
      <MonthGrid
        from={from}
        anchor={anchor}
        items={items}
        tz={tz}
        onOpen={onOpen}
        onPickDay={onPickDay}
      />
    );
  }
  const columns: Column[] =
    mode === 'day' && members.length > 1
      ? members.map((m) => ({
          key: m.membershipId,
          label: m.name,
          date: from,
          membershipId: m.membershipId,
        }))
      : Array.from({ length: days }, (_, i) => {
          const date = addDays(from, i);
          return { key: date, label: dayLabel(date), date };
        });
  const today = todayIn(tz);
  const inColumn = (item: CalendarItem, column: Column) =>
    zonedParts(item.startsAt, tz).date === column.date &&
    (!column.membershipId || item.assignedTo.membershipId === column.membershipId);
  const showWho = members.length > 1;

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-surface">
      <div
        className={cn(
          mode === 'week' ? 'min-w-[44rem]' : columns.length > 2 ? 'min-w-[36rem]' : '',
        )}
      >
        {/* Header and all-day row */}
        <div className="flex border-b border-border">
          <div className="w-14 shrink-0" />
          {columns.map((c) => {
            const allDay = items.filter((i) => i.allDay && inColumn(i, c));
            return (
              <div key={c.key} className="min-w-0 flex-1 border-l border-border p-1.5">
                {mode === 'week' ? (
                  <button
                    type="button"
                    onClick={() => {
                      onPickDay(c.date);
                    }}
                    className={cn(
                      'w-full rounded-md px-1 py-0.5 text-left text-sm font-medium capitalize hover:bg-muted',
                      c.date === today && 'text-primary',
                    )}
                  >
                    {c.label}
                  </button>
                ) : (
                  <p className="px-1 py-0.5 text-sm font-medium">{c.label}</p>
                )}
                <div className="space-y-1">
                  {allDay.map((i, n) => (
                    <Block
                      key={i.busy ? `busy-${String(n)}` : i.id}
                      item={i}
                      tz={tz}
                      showWho={showWho}
                      onOpen={onOpen}
                      compact
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        {/* Hours */}
        <div className="flex">
          <div className="grid w-14 shrink-0 grid-rows-[repeat(64,0.75rem)]">
            {Array.from({ length: END_HOUR - START_HOUR }, (_, h) => (
              <div
                key={h}
                className="row-span-4 -mt-2 pr-2 text-right text-xs text-muted-foreground"
              >
                {String(START_HOUR + h).padStart(2, '0')}:00
              </div>
            ))}
          </div>
          {columns.map((c) => (
            <DayColumn
              key={c.key}
              items={items.filter((i) => !i.allDay && inColumn(i, c))}
              tz={tz}
              showWho={showWho && !c.membershipId}
              onOpen={onOpen}
              label={c.label}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function DayColumn({
  items,
  tz,
  showWho,
  onOpen,
  label,
}: {
  items: CalendarItem[];
  tz: string;
  showWho: boolean;
  onOpen: (activity: ActivityItem) => void;
  label: string;
}) {
  const placed = placeInLanes(items, tz);
  const lanes = Math.max(1, ...placed.map((p) => p.lanes));
  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        'relative grid min-w-0 flex-1 grid-rows-[repeat(64,0.75rem)] border-l border-border',
        GRID_COLS[lanes - 1],
      )}
    >
      {Array.from({ length: END_HOUR - START_HOUR }, (_, h) => (
        <div
          key={h}
          aria-hidden="true"
          className={cn('col-span-full row-span-4 border-t border-border/60', ROW_START[h * 4])}
        />
      ))}
      {placed.map(({ item, lane, lanes: clusterLanes, row, span }, n) => {
        // Alone: the whole width. In a cluster of k blocks: N/k columns each.
        const width = Math.max(1, Math.floor(lanes / clusterLanes));
        const columns =
          clusterLanes === 1 ? 'col-span-full' : cn(COL_START[lane * width], COL_SPAN[width - 1]);
        return (
          <div
            key={item.busy ? `busy-${String(n)}` : item.id}
            className={cn('z-10 min-w-0 p-px', ROW_START[row], ROW_SPAN[span - 1], columns)}
          >
            <Block item={item} tz={tz} showWho={showWho} onOpen={onOpen} short={span <= 2} />
          </div>
        );
      })}
    </div>
  );
}

/** Rows (15-minute slots, clamped to the visible hours) and side-by-side lanes. */
function placeInLanes(items: CalendarItem[], tz: string) {
  const minutes = (value: string) => {
    const [h = 0, m = 0] = zonedParts(value, tz).time.split(':').map(Number);
    return h * 60 + m;
  };
  const rows = items
    .map((item) => {
      const start = minutes(item.startsAt);
      const end = item.endsAt ? minutes(item.endsAt) : start + DEFAULT_ACTIVITY_MINUTES;
      const first = clamp(Math.floor((start - START_HOUR * 60) / SLOT_MINUTES), 0, ROWS - 1);
      const last = clamp(
        Math.ceil((Math.max(end, start + SLOT_MINUTES) - START_HOUR * 60) / SLOT_MINUTES),
        first + 1,
        ROWS,
      );
      return { item, row: first, span: last - first };
    })
    .sort((a, b) => a.row - b.row || b.span - a.span);
  // Lanes per cluster of overlapping blocks: a block that overlaps nothing gets the
  // whole width; blocks that overlap share it side by side.
  const placed: ((typeof rows)[number] & { lane: number; cluster: number })[] = [];
  const lanesOf: number[] = [];
  let laneEnds: number[] = [];
  let clusterEnd = -1;
  for (const r of rows) {
    if (r.row >= clusterEnd) {
      laneEnds = [];
      lanesOf.push(0);
    }
    const cluster = lanesOf.length - 1;
    let lane = laneEnds.findIndex((endRow) => endRow <= r.row);
    if (lane === -1) lane = Math.min(laneEnds.length, MAX_LANES - 1);
    laneEnds[lane] = Math.max(laneEnds[lane] ?? 0, r.row + r.span);
    clusterEnd = Math.max(clusterEnd, r.row + r.span);
    lanesOf[cluster] = Math.max(lanesOf[cluster] ?? 0, lane + 1);
    placed.push({ ...r, lane, cluster });
  }
  return placed.map((p) => ({ ...p, lanes: lanesOf[p.cluster] ?? 1 }));
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function Block({
  item,
  tz,
  showWho,
  onOpen,
  compact = false,
  short = false,
}: {
  item: CalendarItem;
  tz: string;
  showWho: boolean;
  onOpen: (activity: ActivityItem) => void;
  /** All-day row and month cells: one line, natural height. */
  compact?: boolean;
  /** 30 minutes or less: start time and title on one line. */
  short?: boolean;
}) {
  const time = item.allDay
    ? 'Todo el día'
    : `${formatTime(item.startsAt, tz)}${item.endsAt ? `–${formatTime(item.endsAt, tz)}` : ''}`;
  const warning = item.overlap && (
    <span className="inline-flex items-center gap-0.5 font-medium">
      <TriangleAlert className="size-3" aria-hidden="true" />
      <span className="sr-only">Se cruza con otra actividad.</span>
    </span>
  );
  const frame = cn(
    'block w-full overflow-hidden rounded-md border-l-4 px-1.5 py-0.5 text-left text-xs leading-tight',
    item.overlap && 'ring-2 ring-warning ring-inset',
    compact ? 'min-h-6' : 'h-full',
  );
  const oneLine = compact || short;
  // With several people shown: "10:00–11:00 · Carlos Rivas".
  const who = (text: string) => (showWho ? `${text} · ${item.assignedTo.name}` : text);
  if (item.busy) {
    return (
      <div
        className={cn(
          frame,
          'border border-dashed border-muted-foreground/50 bg-muted text-muted-foreground',
        )}
      >
        <p className="flex items-center gap-1 font-medium">
          <Lock className="size-3 shrink-0" aria-hidden="true" />
          <span className="truncate">
            {oneLine && !item.allDay ? `${formatTime(item.startsAt, tz)} ` : ''}Ocupado
          </span>
          {warning}
        </p>
        {!oneLine && <p className="truncate">{who(time)}</p>}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        onOpen(item);
      }}
      className={cn(
        frame,
        BLOCK[item.type.color] ?? BLOCK.blue,
        'text-foreground hover:brightness-95',
        item.status === 'done' && 'opacity-60',
      )}
    >
      <span className="flex items-center gap-1 font-medium">
        {item.isPrivate && <Lock className="size-3 shrink-0" aria-hidden="true" />}
        <span className={cn('truncate', item.status === 'done' && 'line-through')}>
          {oneLine && !item.allDay ? `${formatTime(item.startsAt, tz)} ` : ''}
          {item.title}
        </span>
        {warning}
      </span>
      {!oneLine && <span className="block truncate">{who(time)}</span>}
    </button>
  );
}

function MonthGrid({
  from,
  anchor,
  items,
  tz,
  onOpen,
  onPickDay,
}: {
  from: string;
  anchor: string;
  items: CalendarItem[];
  tz: string;
  onOpen: (activity: ActivityItem) => void;
  onPickDay: (date: string) => void;
}) {
  const today = todayIn(tz);
  const month = anchor.slice(0, 7);
  const byDay = new Map<string, CalendarItem[]>();
  for (const item of items) {
    const date = zonedParts(item.startsAt, tz).date;
    byDay.set(date, [...(byDay.get(date) ?? []), item]);
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-surface">
      <div className="grid min-w-[36rem] grid-cols-7">
        {['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'].map((d) => (
          <p
            key={d}
            className="border-b border-border p-1.5 text-center text-xs font-medium text-muted-foreground"
          >
            {d}
          </p>
        ))}
        {Array.from({ length: 42 }, (_, i) => {
          const date = addDays(from, i);
          const dayItems = byDay.get(date) ?? [];
          return (
            <div
              key={date}
              className={cn(
                'min-h-24 space-y-1 border-b border-l border-border p-1',
                date.slice(0, 7) !== month && 'bg-muted/50',
              )}
            >
              <button
                type="button"
                onClick={() => {
                  onPickDay(date);
                }}
                aria-label={`Ver el ${dayLabel(date, true)}`}
                className={cn(
                  'grid size-7 place-items-center rounded-full text-xs hover:bg-muted',
                  date === today && 'bg-primary text-primary-foreground hover:bg-primary/90',
                )}
              >
                {Number(date.slice(8))}
              </button>
              {dayItems.slice(0, 3).map((item, n) => (
                <Block
                  key={item.busy ? `busy-${String(n)}` : item.id}
                  item={item}
                  tz={tz}
                  showWho={false}
                  onOpen={onOpen}
                  compact
                />
              ))}
              {dayItems.length > 3 && (
                <button
                  type="button"
                  onClick={() => {
                    onPickDay(date);
                  }}
                  className="text-xs text-primary hover:underline"
                >
                  {dayItems.length - 3} más
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
